import React, { useEffect, useState } from "react";
import { Link, useSearchParams, useLocation } from "react-router-dom";
import PublicLayout from "@/components/layout/PublicLayout";
import api from "@/lib/api";
import InGameLeaderboard from "@/components/leaderboards/InGameLeaderboard";
import { FreeProfileCTA } from "@/components/interest/ProfileCTAs";
import { GateScreen, useSubscriberGate } from "@/components/SubscriberGate";
import { Trophy, Filter, ShieldCheck } from "lucide-react";

const METRIC_BOARDS = [
  { key: "cbr_overall_rating", label: "CBR Overall Rating", unit: "", rating: true },
  { key: "exit_velocity", label: "Exit Velocity", unit: "mph" },
  { key: "average_exit_velocity", label: "Avg Exit Velocity", unit: "mph" },
  { key: "fastball_velocity", label: "Fastball Velocity", unit: "mph" },
  { key: "infield_velocity", label: "Infield Velocity", unit: "mph" },
  { key: "outfield_velocity", label: "Outfield Velocity", unit: "mph" },
  { key: "catcher_velocity", label: "Catcher Velocity", unit: "mph" },
  { key: "pop_time", label: "Pop Time", unit: "s" },
  { key: "sixty_yard_sprint", label: "60-Yard", unit: "s" },
  { key: "ten_yard_sprint", label: "10-Yard", unit: "s" },
  { key: "five_ten_five_shuttle", label: "5-10-5 Shuttle", unit: "s" },
];

const PROVINCES = ["ON", "QC", "BC", "AB", "MB", "SK", "NS", "NB", "NL", "PE"];
const POSITIONS = ["RHP", "LHP", "C", "1B", "2B", "3B", "SS", "LF", "CF", "RF", "OF", "UTIL", "DH"];
const GRAD_YEARS = [2025, 2026, 2027, 2028, 2029, 2030];

export default function Leaderboards() {
  const location = useLocation();
  const [params, setParams] = useSearchParams();
  const [board, setBoard] = useState(METRIC_BOARDS[0]);
  const [camps, setCamps] = useState([]);
  const [filters, setFilters] = useState({ camp_id: "", graduation_year: "", primary_position: "", province: "" });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const { loading: gateLoading, blocked, message } = useSubscriberGate();

  const mode = (location.pathname.endsWith("/statistics") || params.get("view") === "ingame") ? "ingame" : "metrics";
  const setMode = (m) => { const p = new URLSearchParams(params); if (m === "ingame") p.set("view", "ingame"); else p.delete("view"); setParams(p, { replace: true }); };

  useEffect(() => {
    api.get("/camps").then(({ data }) => setCamps(data.items || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (gateLoading || blocked) { setLoading(false); return; }
    setLoading(true);
    const params = {};
    if (filters.graduation_year) params.graduation_year = filters.graduation_year;
    if (filters.primary_position) params.primary_position = filters.primary_position;
    if (filters.province) params.province = filters.province;

    const req = board.rating
      ? api.get("/rankings", { params: { ...params, limit: 100 } }).then(({ data }) =>
          (data.rankings || []).map((r) => ({
            rank: r.rank, player: r, value: r.cbr_overall_rating, units: "",
          }))
        )
      : api
          .get("/leaderboards/global", {
            params: { metric_type: board.key, ...params, ...(filters.camp_id ? { camp_id: filters.camp_id } : {}), limit: 100 },
          })
          .then(({ data }) => (data.leaderboard || []).map((e) => ({ rank: e.rank, player: e.player, value: e.value, units: e.units })));

    req.then(setRows).catch(() => setRows([])).finally(() => setLoading(false));
  }, [board, filters, gateLoading, blocked]);

  const setF = (k, v) => setFilters((f) => ({ ...f, [k]: v }));

  return (
    <PublicLayout>
      <section className="bg-[#0A192F] text-white border-b-4 border-[#E63946]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-12">
          <div className="label-overline text-[#E63946]">CBR Verified Data</div>
          <h1 data-testid="leaderboards-title" className="font-display text-4xl sm:text-5xl lg:text-6xl uppercase tracking-tighter mt-2 leading-[0.95]">
            Leaderboards
          </h1>
          <p className="text-sm text-white/70 mt-4 max-w-2xl">
            Every result below is CBR Verified or staff-entered at an official event. Self-reported numbers never appear here.
          </p>
        </div>
      </section>

      {/* Mode toggle: Metrics | In-Game Statistics */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex gap-2">
          <button data-testid="lb-mode-metrics" onClick={() => setMode("metrics")} aria-pressed={mode === "metrics"}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border ${mode === "metrics" ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>Metrics</button>
          <button data-testid="lb-mode-ingame" onClick={() => setMode("ingame")} aria-pressed={mode === "ingame"}
            className={`px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm border ${mode === "ingame" ? "bg-[#E63946] text-white border-[#E63946]" : "bg-white border-slate-300 text-[#0A192F] hover:bg-slate-50"}`}>In-Game Statistics</button>
        </div>
      </section>

      {gateLoading ? (
        <section className="bg-[#F8F9FA] py-16 min-h-[400px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center text-sm text-slate-400">Loading…</div>
        </section>
      ) : blocked ? (
        <section className="bg-[#F8F9FA] py-16 min-h-[400px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8"><GateScreen message={message} /></div>
        </section>
      ) : mode === "ingame" ? (
        <section className="bg-[#F8F9FA] py-8 min-h-[500px]">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <InGameLeaderboard />
          </div>
        </section>
      ) : (
      <>
      {/* Board selector */}
      <section className="bg-white border-b border-slate-200 sticky top-16 z-30">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-3 flex gap-2 overflow-x-auto">
          {METRIC_BOARDS.map((b) => (
            <button
              key={b.key}
              data-testid={`board-tab-${b.key}`}
              onClick={() => setBoard(b)}
              className={`whitespace-nowrap px-4 py-2 text-xs font-semibold uppercase tracking-wider rounded-sm ${
                board.key === b.key ? "bg-[#E63946] text-white" : "bg-white border border-slate-300 text-[#0A192F] hover:bg-slate-50"
              }`}
            >
              {b.label}
            </button>
          ))}
        </div>
      </section>

      <section className="bg-[#F8F9FA] py-8 min-h-[500px]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          {/* Filters */}
          <div className="flex flex-wrap items-end gap-3 mb-6">
            <div className="flex items-center gap-1 label-overline text-slate-500"><Filter className="w-3.5 h-3.5" /> Filters</div>
            {!board.rating && (
              <Select testid="filter-camp" label="Camp / Event" value={filters.camp_id} onChange={(v) => setF("camp_id", v)}>
                <option value="">All Camps</option>
                {camps.map((c) => <option key={c.id} value={c.id}>{c.title}</option>)}
              </Select>
            )}
            <Select testid="filter-grad-year" label="Grad Year" value={filters.graduation_year} onChange={(v) => setF("graduation_year", v)}>
              <option value="">All</option>
              {GRAD_YEARS.map((y) => <option key={y} value={y}>{y}</option>)}
            </Select>
            <Select testid="filter-position" label="Position" value={filters.primary_position} onChange={(v) => setF("primary_position", v)}>
              <option value="">All</option>
              {POSITIONS.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
            <Select testid="filter-province" label="Province" value={filters.province} onChange={(v) => setF("province", v)}>
              <option value="">All</option>
              {PROVINCES.map((p) => <option key={p} value={p}>{p}</option>)}
            </Select>
          </div>

          <div className="bg-white border border-slate-200 rounded-sm overflow-x-auto">
            {loading ? (
              <div className="p-10 label-overline text-slate-500">Loading…</div>
            ) : rows.length === 0 ? (
              <div className="p-12 text-center">
                <Trophy className="w-12 h-12 mx-auto text-slate-300" />
                <div className="font-display text-2xl uppercase mt-4">No Results</div>
                <p className="text-sm text-slate-600 mt-2">No verified results match these filters yet.</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-slate-50 border-b border-slate-200">
                    <th className="text-left p-3 label-overline w-12">Rank</th>
                    <th className="text-left p-3 label-overline">Player</th>
                    <th className="text-left p-3 label-overline">Class</th>
                    <th className="text-left p-3 label-overline">Pos</th>
                    <th className="text-left p-3 label-overline">Province</th>
                    <th className="text-left p-3 label-overline">{board.rating ? "Rating" : "Result"}</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((e, i) => (
                    <tr key={`${e.player?.id}-${i}`} data-testid={`lb-row-${e.player?.id}`} className={`border-b border-slate-100 last:border-0 ${i < 3 ? "bg-amber-50/40" : ""}`}>
                      <td className="p-3 font-data text-lg">
                        {i === 0 ? <span className="text-[#E63946]">#1</span> : i === 1 ? <span className="text-[#0A192F]">#2</span> : i === 2 ? <span className="text-amber-700">#3</span> : <span className="text-slate-500">#{e.rank}</span>}
                      </td>
                      <td className="p-3">
                        {e.player?.slug ? (
                          <Link to={`/prospects/${e.player.slug}`} className="font-semibold hover:text-[#E63946]">
                            {e.player.first_name} {e.player.last_name}
                          </Link>
                        ) : (
                          <span className="font-semibold">{e.player?.first_name} {e.player?.last_name}</span>
                        )}
                      </td>
                      <td className="p-3 font-data">{e.player?.graduation_year || "—"}</td>
                      <td className="p-3 font-data">{e.player?.primary_position || "—"}</td>
                      <td className="p-3">{e.player?.province || "—"}</td>
                      <td className="p-3 font-data text-lg text-[#0A192F]">
                        {board.rating ? Number(e.value).toFixed(1) : e.value}
                        {e.units && <span className="text-xs text-slate-500 ml-1">{e.units}</span>}
                        {!board.rating && <ShieldCheck className="inline w-3.5 h-3.5 text-[#E63946] ml-2" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        </div>
      </section>
      </>
      )}
      <section className="bg-[#F1F3F6] py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <FreeProfileCTA />
        </div>
      </section>
    </PublicLayout>
  );
}

function Select({ label, value, onChange, children, testid }) {
  return (
    <label className="flex flex-col gap-1">
      <span className="label-overline text-[10px]">{label}</span>
      <select
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="border border-slate-300 rounded-sm px-3 py-2 text-sm bg-white min-w-[140px]"
      >
        {children}
      </select>
    </label>
  );
}
